import { App, Select } from 'antd';
import { AxiosResponse } from 'axios';
import { useCallback, useState } from 'react';
import { PaymentHttp } from '@/services';
import { AppButton, Flex, PriceSwiper, InputNumeric } from '@/components';
import { envConfig, GatewayConsumer, PAYMENT_RESULT_URL } from '@/configs';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
type GatewayRes = AxiosResponse<{ payload: PaymentHttp.ProcessPaymentResponse }, any>;

export interface GatewayInfoProps {
  gatewayInfo: GatewayInfoState;
}

const IncrementCredit = ({ gatewayInfo }: GatewayInfoProps = {
  gatewayInfo: {} as any
}) => {

  const [t] = useTranslation(['common', 'payment']);
  const [loading, setLoading] = useState(false);
  const [skipData, setSkipData] = useState<GatewayRes>(null);
  const [gatewayId, setGatewayId] = useState(undefined);
  const [amountInToman, setAmountInToman] = useState<string>();
  const [customAmount, setCustomAmount] = useState<number>(undefined);
  const { message } = App.useApp();

  const amounts = [...(gatewayInfo?.amounts?.map(x => x / 10).sort((a, b) => a - b) ?? [])] as any[];

  gatewayInfo.canCustomAmountPay = true;
  if (gatewayInfo?.canCustomAmountPay) {
    amounts.push('custom');
  }

  const postForm = useCallback(async (data: PaymentHttp.ProcessPaymentResponse) => {
    const form = document.createElement('form');
    document.body.appendChild(form);
    form.method = 'post';
    form.action = data.bankUrl;
    for (const [name, value] of Object.entries(data)) {
      if (['httpMethod', 'bankUrl'].includes(name)) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
    form.submit();
  }, []);

  const finalize = useCallback(async (res: GatewayRes) => {
    try {
      setLoading(true);
      setSkipData(null);
      const status = res.status;
      const payload = res.data.payload;
      if (res.status === 302) {
        window.location.href = payload.url;
      }
      else if (status === 200 && payload?.httpMethod != null) {
        postForm(payload);
      }
      else {
        message.error({ content: t('payment:errors.gateway-connect') + ' - 101' });
      }
    }
    catch (e) {
      message.error({ content: t('payment:errors.gateway-connect') + ' - 100' });
    }
    finally {
      setLoading(false);
    }
  }, [t, message, postForm]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    /* Convert amount to Rial Currency */
    const finalAmount = (amountInToman === 'custom' ? Number(customAmount) : Number(amountInToman)) * 10;
    try {
      const res = await PaymentHttp.checkout({
        amount: finalAmount,
        gatewayId,
        callBackAppUrl: PAYMENT_RESULT_URL,
        consumer: GatewayConsumer.CreditCharge,
        currentPhase: 1
      });
      setSkipData(res);
    }
    catch (e) {
      message.error({ content: t('payment:errors.gateway-connect') + ' - 100' });
    }
    finally {
      setLoading(false);
    }
  }, [gatewayId, amountInToman, customAmount, t, message]);

  const isDisabled = () => {
    if (!gatewayId) return true;
    if (amountInToman === 'custom' && customAmount == null) return true;
    return false;
  };

  return (
    <Flex direction="column" className="mt-auto">
      <Flex direction="column" gap={10}>
        <PriceSwiper
          values={amounts}
          selected={amountInToman}
          onSelect={key => {
            setAmountInToman(key);
            if (key !== 'custom' && customAmount != null) {
              setCustomAmount(undefined);
            }
          }}
        />
        {gatewayInfo.canCustomAmountPay &&
          <InputNumeric
            size="large"
            value={customAmount}
            disabled={amountInToman !== 'custom'}
            className="inline-flex"
            onChange={value => setCustomAmount(value as number)}
            placeholder={`${t('payment:labels.custom-amount-min')}(${Math.min(...gatewayInfo.amounts)} ${envConfig.ui.removeCurrency ? '' : t('common:labels.tomans')})`}
            style={{ fontSize: '0.875rem', border: '2px solid #d9d8d8' }}
          />
        }
        <Select
          size="large"
          value={gatewayId}
          className="select-gateway"
          defaultValue="select-gateway"
          onChange={value => setGatewayId(value)}
        >
          <Option key="select-gateway">
            {t('payment:labels.select-gateway')}
          </Option>
          {gatewayInfo?.banks?.map(bank => (
            <Option key={bank.id}>{bank.displayName}</Option>
          ))}
        </Select>
        <AppButton
          size="middle"
          appVariant="submit"
          loading={loading}
          disabled={isDisabled()}
          onClick={handleSubmit}
        >
          {t('common:labels.pay')}
        </AppButton>
        {skipData && (
          <AppButton type="dashed" onClick={() => finalize(skipData)}>
            SKIP TO GATEWAY
          </AppButton>
        )}
      </Flex>
    </Flex>
  );
};

export default IncrementCredit;
